<script setup lang="ts">
import { nextTick, onMounted, type Ref, ref } from 'vue';
import { Cropper } from 'vue-advanced-cropper';
import cloneDeep from 'lodash.clonedeep';
import {
  RBtn,
  useLoadingSpinnerStore,
} from '@therobotcompany/robco-frontend-library';
import { EMPTY_USER, type User } from '@/models/User';
import { patchUserDetails } from '@/api/UserApi';
import 'vue-advanced-cropper/dist/style.css';
import { convertFileToBase64, convertUrlToFile } from '@/helpers/FileHelper';
import crop from '@/helpers/ImageHelper';
import gettext from '@/i18n/gettext';
import CoreApi from '@/api/CoreApi';
import useAuthStore from '@/store/AuthStore';

const { $gettext } = gettext;
const authStore = useAuthStore();
const loadingSpinnerStore = useLoadingSpinnerStore();

const editMode = ref(false);
const saveError = ref(false);
const user: Ref<User> = ref(cloneDeep(EMPTY_USER));
const pictureUpload: Ref<File | undefined> = ref(undefined);
const showCropDialog = ref(false);
const pictureToCrop = ref('');
const croppedPicture = ref('');
const profilePictureUpload = ref();
const editNameField = ref();

/**
 * Make a copy of the user object and store it in a local variable.
 * This is needed because if we would use the user object directly from the store,
 * it would lead to invalid direct mutations of the Vuex store object
 * as soon as we modify its values.
 */
async function loadUser() {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    await authStore.loadProfileOnce();
    user.value = cloneDeep(authStore.user);
  } catch (error) {
    console.error(error);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
}

function editUser() {
  editMode.value = true;
  saveError.value = false;
  nextTick(() => {
    editNameField.value.focus();
  });
}

async function saveUser() {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    await patchUserDetails(user.value);
    loadUser();
    editMode.value = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    console.error(err);
    saveError.value = true;
    loadUser();
    editMode.value = false;
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function onPictureResize(data: any) {
  convertUrlToFile(
    data.image.src,
    pictureUpload.value!.name,
    pictureUpload.value!.type,
  ).then((img) => {
    crop(
      img,
      data.coordinates.width,
      data.coordinates.height,
      data.coordinates.left,
      data.coordinates.top,
    ).then((image) => {
      croppedPicture.value = image as string;
    });
  });
}

function showCroppingDialog() {
  if (pictureUpload.value && pictureUpload.value.name) {
    convertFileToBase64(pictureUpload.value).then((result) => {
      pictureToCrop.value = result as string;
      showCropDialog.value = true;
    });
  }
}

function hideCroppingDialog() {
  showCropDialog.value = false;
  pictureUpload.value = undefined;
}

function uploadProfilePicture() {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  convertUrlToFile(
    croppedPicture.value,
    pictureUpload.value!.name,
    pictureUpload.value!.type,
  ).then((croppedImage) => {
    const formData = new FormData();
    formData.append('profile_pic', croppedImage);
    CoreApi.axiosClient.patch('/user/profile-picture', formData).then(
      () => {
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      },
      (error) => {
        // Could not put new userdata
        console.error(error);
        showCropDialog.value = false;
        pictureUpload.value = undefined;
        pictureToCrop.value = '';
        croppedPicture.value = '';
        loadingSpinnerStore.setShowLoadingOverlay(false);
      },
    );
  });
}

onMounted(() => {
  loadUser();
});
</script>

<template>
  <div v-if="user.uuid !== ''" class="pa-4 pa-sm-12">
    <v-dialog v-model="showCropDialog" persistent width="800px" height="600px">
      <v-card>
        <v-card-title>
          {{ $gettext('Profile picture upload') }}
        </v-card-title>
        <v-card-subtitle>
          {{ $gettext('Select a section for your profile picture') }}
        </v-card-subtitle>
        <v-card-text>
          <cropper
            class="cropper"
            :src="pictureToCrop"
            :stencil-props="{
              aspectRatio: 1,
            }"
            @change="onPictureResize"
          />
        </v-card-text>
        <v-card-actions>
          <r-btn variant="outlined" @click="hideCroppingDialog">
            {{ $gettext('Cancel') }}
          </r-btn>
          <v-spacer></v-spacer>
          <r-btn @click="uploadProfilePicture()">{{
            $gettext('Upload')
          }}</r-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row class="text-center">
      <v-col cols="12">
        <v-hover v-slot="{ isHovering, props }">
          <v-avatar size="128" v-bind="props" class="color-unset">
            <v-img
              v-if="user.profilePic"
              :src="user.profilePic"
              class="my-3"
              contain
              height="200"
            />
            <v-icon
              v-else
              size="128"
              icon="mdi-account"
              color="on-background"
            ></v-icon>

            <v-avatar
              v-if="isHovering"
              class="user_image_hover color-unset"
              size="128"
              @click="profilePictureUpload.click()"
            >
              <v-icon icon="mdi-pencil"></v-icon>
            </v-avatar>
            <v-file-input
              ref="profilePictureUpload"
              v-model="pictureUpload"
              style="display: none"
              accept="image/png, image/jpeg, image/bmp"
              :multiple="false"
              @update:modelValue="showCroppingDialog"
            ></v-file-input>
          </v-avatar>
        </v-hover>
      </v-col>
    </v-row>
    <v-row class="text-center">
      <v-col class="mb-4">
        <h1 class="display-2 mb-3">
          {{ $gettext('Profile') }}
        </h1>
      </v-col>
    </v-row>
    <div class="text-center">
      <v-table style="background: unset; max-width: 400px" class="mx-auto">
        <colgroup>
          <col span="1" />
          <col span="1" />
        </colgroup>
        <thead>
          <tr>
            <th class="text-left">
              {{ $gettext('Property') }}
            </th>
            <th class="text-left">
              {{ $gettext('Value') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr class="text-left">
            <td>{{ $gettext('Email') }}</td>
            <td>{{ user.email }}</td>
          </tr>
          <tr class="text-left">
            <td>{{ $gettext('Name') }}</td>
            <td v-if="!editMode" class="cursor-pointer" @click="editUser()">
              {{ user.name }}
            </td>
            <td v-if="editMode">
              <v-text-field
                ref="editNameField"
                v-model="user.name"
                class="mb-2"
                density="compact"
                hide-details
                variant="underlined"
                v-on:keyup.enter="saveUser()"
              />
            </td>
          </tr>
        </tbody>
      </v-table>
      <v-alert
        v-if="saveError"
        border="top"
        border-color="error"
        class="_float-left mt-3"
      >
        <strong>{{
          $gettext('Could not save profile. Please try again later!')
        }}</strong>
      </v-alert>
      <r-btn
        v-if="!editMode"
        variant="accent"
        style="width: 150px"
        class="mt-6"
        @click="editUser()"
      >
        {{ $gettext('Edit') }}
      </r-btn>
      <r-btn
        v-if="editMode"
        variant="accent"
        style="width: 150px"
        class="mt-6"
        @click="saveUser()"
      >
        {{ $gettext('Save') }}
      </r-btn>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.cropper {
  height: 400px;
  width: 100%;
}
.user_image_hover {
  cursor: pointer;
  background-color: #fff3;
  position: absolute;
  inset: 0;
}
</style>
